import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../providers/ThemeProvider';
import { CalendarIcon, SearchIcon, MessageCircleIcon, MusicIcon, ClockIcon, VideoIcon } from 'lucide-react';
export const StudentDashboard = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  // Mock data - UI only, no backend logic
  const studentName = 'Alex';
  const hasUpcomingLessons = true; // Toggle this to see empty state
  // Mock lesson data - unified list with first lesson emphasized
  const allLessons = [{
    id: 1,
    teacherName: 'Sarah Johnson',
    teacherAvatar: 'SJ',
    instrument: 'Piano',
    date: 'Today',
    time: '4:00 PM',
    duration: '60 min',
    format: 'Virtual',
    status: 'Confirmed',
    isNext: true // First lesson is emphasized
  }, {
    id: 2,
    teacherName: 'Michael Chen',
    teacherAvatar: 'MC',
    instrument: 'Guitar',
    date: 'Wed, Jan 8',
    time: '3:00 PM',
    duration: '45 min',
    format: 'Virtual',
    status: 'Confirmed',
    isNext: false
  }, {
    id: 3,
    teacherName: 'Sarah Johnson',
    teacherAvatar: 'SJ',
    instrument: 'Piano',
    date: 'Fri, Jan 10',
    time: '4:00 PM',
    duration: '60 min',
    format: 'Virtual',
    status: 'Confirmed',
    isNext: false
  }];
  const quickActions = [{
    label: 'My Schedule',
    icon: CalendarIcon,
    onClick: () => navigate('/student-schedule'),
    color: 'blue'
  }, {
    label: 'Find Teachers',
    icon: SearchIcon,
    onClick: () => navigate('/find-teachers'),
    color: 'green'
  }, {
    label: 'Messages',
    icon: MessageCircleIcon,
    onClick: () => navigate('/messages'),
    color: 'purple'
  }, {
    label: 'Sheet Music',
    icon: MusicIcon,
    onClick: () => navigate('/sheet-music'),
    color: 'pink'
  }];
  return <div className="min-h-screen p-6 md:p-8" data-id="element-6690">
      <div className="max-w-4xl mx-auto space-y-8" data-id="element-6691">
        {/* Welcome Header */}
        <div data-id="element-6692">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6693">
            Welcome back, {studentName}
          </h1>
          <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6694">
            {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>

        {/* Unified Upcoming Lessons Section */}
        <div data-id="element-6695">
          <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6696">
            Upcoming Lessons
          </h2>

          {hasUpcomingLessons ? <div className="space-y-3" data-id="element-6697">
              {allLessons.map(lesson => <div key={lesson.id} className={`rounded-lg p-5 shadow-sm border transition-all ${lesson.isNext ? isDark ? 'bg-navy-800 border-blue-500/30 shadow-blue-500/10' : 'bg-white border-blue-200 shadow-blue-100' : isDark ? 'bg-navy-800 border-navy-700 hover:bg-navy-750' : 'bg-white border-gray-200 hover:bg-gray-50'}`} data-id="element-6698">
                  <div className="flex items-center justify-between gap-4" data-id="element-6699">
                    <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-6700">
                      {/* Teacher Avatar */}
                      <div className={`${lesson.isNext ? 'w-12 h-12' : 'w-10 h-10'} rounded-full flex items-center justify-center ${lesson.isNext ? 'text-base' : 'text-sm'} font-semibold flex-shrink-0 ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`} data-id="element-6701">
                        {lesson.teacherAvatar}
                      </div>

                      {/* Lesson info */}
                      <div className="flex-1 min-w-0" data-id="element-6702">
                        <div className="flex items-center gap-2 mb-1" data-id="element-6703">
                          <h3 className={`${lesson.isNext ? 'text-base' : 'text-sm'} font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6704">
                            {lesson.instrument}
                          </h3>
                          {lesson.isNext && <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}`} data-id="element-6705">
                              {lesson.status}
                            </span>}
                          <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-navy-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`} data-id="element-6706">
                            {lesson.duration}
                          </span>
                        </div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`} data-id="element-6707">
                          with {lesson.teacherName}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-sm" data-id="element-6708">
                          <div className="flex items-center gap-1.5" data-id="element-6709">
                            <ClockIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6710" />
                            <span className={isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-6711">
                              {lesson.date} at {lesson.time}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5" data-id="element-6712">
                            <VideoIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6713" />
                            <span className={isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-6714">
                              {lesson.format}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2 flex-shrink-0" data-id="element-6715">
                      {lesson.isNext ? <>
                          <button className="px-5 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-6716">
                            Join Lesson
                          </button>
                          <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} data-id="element-6717">
                            View Details
                          </button>
                        </> : <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} data-id="element-6718">
                          View
                        </button>}
                    </div>
                  </div>
                </div>)}
            </div> :
        // Empty state - No upcoming lessons
        <div className={`rounded-xl p-12 text-center border-2 border-dashed ${isDark ? 'bg-navy-800/50 border-navy-700' : 'bg-gray-50 border-gray-300'}`} data-id="element-6719">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-6720">
                <CalendarIcon className={`h-8 w-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6721" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6722">
                No upcoming lessons
              </h3>
              <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6723">
                Ready to start learning? Find a teacher and book your first
                lesson.
              </p>
              <button onClick={() => navigate('/find-teachers')} className="px-6 py-2.5 bg-deep-pink hover:bg-burgundy text-white rounded-lg font-medium transition-colors" data-id="element-6724">
                Find a Teacher
              </button>
            </div>}
        </div>

        {/* Quick Actions */}
        <div data-id="element-6725">
          <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6726">
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-6727">
            {quickActions.map(action => {
            const Icon = action.icon;
            return <button key={action.label} onClick={action.onClick} className={`p-5 rounded-lg shadow-sm border transition-all hover:shadow-md hover:-translate-y-0.5 ${isDark ? 'bg-navy-800 border-navy-700 hover:bg-navy-750' : 'bg-white border-gray-200 hover:bg-gray-50'}`} data-id="element-6728">
                  <div className={`w-10 h-10 mx-auto mb-2.5 rounded-full flex items-center justify-center ${action.color === 'blue' ? isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600' : action.color === 'green' ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600' : action.color === 'purple' ? isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600' : isDark ? 'bg-pink-500/20 text-pink-400' : 'bg-pink-100 text-pink-600'}`} data-id="element-6729">
                    <Icon className="h-5 w-5" data-id="element-6730" />
                  </div>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6731">
                    {action.label}
                  </p>
                </button>;
          })}
          </div>
        </div>
      </div>
    </div>;
};